import { Injectable } from '@angular/core';
import { Apollo, MutationResult } from 'apollo-angular';
import { Observable, map } from 'rxjs';

import {
  ISignBuyListResult,
  IProcessBuyListInput,
  IProcessBuyListResult,
  IAllBuyListFromOrganizationInput,
  IAllBuyListFromOrganizationResult,
  ISharePropertyWithBuyListsInput,
  ISharePropertyWithBuyListsResult,
  IIsBuyListCreationAllowedResult,
  IShareOrganizationWithBuyListsInput,
  IShareOrganizationWithBuyListsResult,
  IListBuyListAliasesInput,
  IListBuyListAliasesResult,
  IUpdateBuyListNameInput,
  IUpdateBuyListNameResult,
  IDeleteUserBuyListInput,
  IDeleteUserBuyListResult,
  IUpdateBuyListAliasResult,
  IUpdateBuyListAliasInput,
} from '@shared/models/buyers-list';
import {
  SIGN_BUY_LIST,
  ALL_BUY_LIST_FROM_ORGANIZATION,
  SHARE_PROPERTY_WITH_BUY_LISTS,
  IS_BUY_LIST_CREATION_ALLOWED,
  LIST_BUY_LIST_ALIASES,
} from '@graphql/queries/buyers-list.queries';
import {
  DELETE_USER_BUY_LIST,
  PROCESS_BUY_LIST,
  SHARE_ORGANIZATION_WITH_BUY_LISTS,
  UPDATE_BUY_LIST_NAME,
  UPDATE_BUY_LIST_ALIAS,
} from '@graphql/mutations/buyers-list.mutations';

@Injectable({
  providedIn: 'root',
})
export class BuyersListService {
  constructor(private apollo: Apollo) {}

  signBuyList(dataType: 'CSV'): Observable<ISignBuyListResult> {
    return this.apollo
      .query({
        query: SIGN_BUY_LIST,
        variables: { dataType },
      })
      .pipe(map((result) => result.data));
  }

  processBuyList(
    input: IProcessBuyListInput
  ): Observable<MutationResult<IProcessBuyListResult>> {
    return this.apollo
      .mutate({
        mutation: PROCESS_BUY_LIST,
        variables: input,
        errorPolicy: 'all',
      })
      .pipe(map((result) => result));
  }

  allBuyListFromOrganization(
    input: IAllBuyListFromOrganizationInput
  ): Observable<IAllBuyListFromOrganizationResult> {
    return this.apollo
      .query({
        query: ALL_BUY_LIST_FROM_ORGANIZATION,
        variables: input,
      })
      .pipe(map((result) => result.data));
  }

  sharePropertyWithBuyLists(
    input: ISharePropertyWithBuyListsInput
  ): Observable<MutationResult<ISharePropertyWithBuyListsResult>> {
    return this.apollo
      .query({
        query: SHARE_PROPERTY_WITH_BUY_LISTS,
        variables: input,
        errorPolicy: 'all',
      })
      .pipe(map((result) => result));
  }

  isBuyListCreationAllowed(): Observable<IIsBuyListCreationAllowedResult> {
    return this.apollo
      .query({
        query: IS_BUY_LIST_CREATION_ALLOWED,
      })
      .pipe(map((result) => result.data));
  }

  shareOrganizationWithBuyLists(
    input: IShareOrganizationWithBuyListsInput
  ): Observable<MutationResult<IShareOrganizationWithBuyListsResult>> {
    return this.apollo
      .mutate({
        mutation: SHARE_ORGANIZATION_WITH_BUY_LISTS,
        variables: input,
        errorPolicy: 'all',
      })
      .pipe(map((result) => result));
  }

  listBuyListAliases(
    input: IListBuyListAliasesInput
  ): Observable<IListBuyListAliasesResult> {
    return this.apollo
      .query({
        query: LIST_BUY_LIST_ALIASES,
        variables: input,
      })
      .pipe(map((result) => result.data));
  }

  updateBuyListName(
    input: IUpdateBuyListNameInput
  ): Observable<IUpdateBuyListNameResult | null | undefined> {
    return this.apollo
      .mutate<IUpdateBuyListNameResult, IUpdateBuyListNameInput>({
        mutation: UPDATE_BUY_LIST_NAME,
        variables: input,
        errorPolicy: 'all',
      })
      .pipe(map((result) => result.data));
  }

  deleteUserBuyList(
    input: IDeleteUserBuyListInput
  ): Observable<IDeleteUserBuyListResult | null | undefined> {
    return this.apollo
      .mutate<IDeleteUserBuyListResult, IDeleteUserBuyListInput>({
        mutation: DELETE_USER_BUY_LIST,
        variables: input,
        errorPolicy: 'all',
      })
      .pipe(map((result) => result.data));
  }

  updateBuyListAlias(
    input: IUpdateBuyListAliasInput
  ): Observable<IUpdateBuyListAliasResult | null | undefined> {
    return this.apollo
      .mutate<IUpdateBuyListAliasResult, IUpdateBuyListAliasInput>({
        mutation: UPDATE_BUY_LIST_ALIAS,
        variables: input,
        errorPolicy: 'all',
      })
      .pipe(map((result) => result.data));
  }
}
